"use client"

import type { LucideIcon } from "lucide-react"
import {
  Ban,
  BookOpen,
  Boxes,
  CalendarDays,
  FileText,
  HandHeart,
  Landmark,
  PiggyBank,
  ReceiptText,
  RotateCcw,
  ScanSearch,
  Search,
  User,
  UserCog,
  Users,
  WalletCards,
} from "lucide-react"
import { Link } from "react-router-dom"

import { HomeTile, HomeTileEmpty } from "@/features/dashboard/home-d4/components/home-tile"
import {
  InvestigationRecentKind,
  type InvestigationRecentEntry,
} from "@/features/finance/stores/investigation-recents-store"
import { formatShortRelativeTime } from "@/shared/lib/dates/date-fns-locale"
import { useLocale, useTranslations } from "@/i18n"

export const INVESTIGATION_KIND_ICONS: Record<InvestigationRecentKind, LucideIcon> = {
  [InvestigationRecentKind.Transaction]: FileText,
  [InvestigationRecentKind.Journal]: BookOpen,
  [InvestigationRecentKind.Account]: Landmark,
  [InvestigationRecentKind.Fund]: PiggyBank,
  [InvestigationRecentKind.Activity]: CalendarDays,
  [InvestigationRecentKind.BudgetLine]: WalletCards,
  [InvestigationRecentKind.Department]: Boxes,
  [InvestigationRecentKind.Ministry]: HandHeart,
  [InvestigationRecentKind.Group]: Users,
  [InvestigationRecentKind.Member]: User,
  [InvestigationRecentKind.User]: UserCog,
  [InvestigationRecentKind.Search]: Search,
  [InvestigationRecentKind.MissingEvidence]: ReceiptText,
  [InvestigationRecentKind.Reversed]: RotateCcw,
  [InvestigationRecentKind.Voided]: Ban,
  [InvestigationRecentKind.Explorer]: ScanSearch,
}

/**
 * "Enquêtes récentes": the last investigations opened from this device in this workspace + branch, newest
 * first. Each row reopens the exact URL it was first opened at (access is re-checked server-side on arrival).
 */
export function RecentInvestigationsCard({
  recents,
  onReopen,
}: {
  recents: InvestigationRecentEntry[]
  onReopen: (entry: InvestigationRecentEntry) => void
}) {
  const t = useTranslations("finance.audit.launcher.recents")
  const locale = useLocale()

  return (
    <HomeTile title={t("title")} testId='audit-recent-investigations'>
      {recents.length === 0 ? (
        <HomeTileEmpty message={t("empty")} />
      ) : (
        <ul className='flex flex-col'>
          {recents.map((entry) => {
            const Icon = INVESTIGATION_KIND_ICONS[entry.kind] ?? ScanSearch
            const kindLabel = t(`kinds.${entry.kind}` as never)
            const when = formatShortRelativeTime(entry.openedAt, locale)
            return (
              <li key={entry.href} className='border-t border-border/60'>
                <Link
                  to={entry.href}
                  data-testid='recent-investigation-row'
                  onClick={() => onReopen(entry)}
                  className='group flex items-center gap-2.5 rounded-md py-[7px] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
                >
                  <Icon aria-hidden className='size-[15px] shrink-0 text-muted-foreground' />
                  <span className='flex min-w-0 flex-col'>
                    <span className='truncate text-[13px] font-bold text-foreground group-hover:underline'>
                      {entry.label ?? kindLabel}
                    </span>
                    <span className='truncate text-[11.5px] text-muted-foreground'>
                      {entry.label ? `${kindLabel} · ${when}` : when}
                    </span>
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </HomeTile>
  )
}
